import React from 'react';
import { Navigate } from 'react-router-dom';
import { Row, Col, Card, Table, Badge } from 'react-bootstrap';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const PRIMARY = '#0c6642';
const PRIMARY_DARK = '#02402b';
const ACCENT = '#fcbe2d';
const IG_COLOR = '#C13584';
const FB_COLOR = '#1877F2';
const GREEN_LIGHT = '#28923a';
const MALE_COLOR = '#405DE6';
const FEMALE_COLOR = '#E1306C';

// ── KPI card ────────────────────────────────────────────────────────────────
interface KpiProps {
  icon: string;
  label: string;
  value: string;
  change?: string;
  positive?: boolean;
  color?: string;
}
const Kpi: React.FC<KpiProps> = ({ icon, label, value, change, positive = true, color = PRIMARY }) => (
  <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: 12 }}>
    <Card.Body className="d-flex flex-column gap-2 p-3">
      <div className="d-flex align-items-center gap-2">
        <div style={{ background: color + '18', borderRadius: 8, padding: '6px 10px', fontSize: '1.3rem' }}>
          <i className={`bi bi-${icon}`} style={{ color }}></i>
        </div>
        <small className="text-muted" style={{ fontSize: '0.78rem', lineHeight: 1.2 }}>{label}</small>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: PRIMARY_DARK, lineHeight: 1 }}>{value}</div>
      {change && (
        <div style={{ fontSize: '0.78rem', color: positive ? GREEN_LIGHT : '#dc3545', fontWeight: 600 }}>
          <i className={`bi bi-arrow-${positive ? 'up' : 'down'}-short`}></i> {change}
        </div>
      )}
    </Card.Body>
  </Card>
);

// ── Section title ────────────────────────────────────────────────────────────
const Section: React.FC<{ icon: string; title: string; color?: string }> = ({ icon, title, color = PRIMARY }) => (
  <div className="d-flex align-items-center gap-2 mb-3 mt-4">
    <div style={{ width: 4, height: 24, background: color, borderRadius: 2 }} />
    <i className={`bi bi-${icon}`} style={{ color, fontSize: '1.1rem' }}></i>
    <h5 className="mb-0 fw-bold" style={{ color: PRIMARY_DARK }}>{title}</h5>
  </div>
);

// ── Data ─────────────────────────────────────────────────────────────────────
const platformData = [
  { name: 'Instagram', visualizacoes: 752029, organico: 659193, anuncios: 92836 },
  { name: 'Facebook', visualizacoes: 89757, organico: 3548, anuncios: 86209 },
];

const fbFormatsData = [
  { name: 'Links', value: 48798 },
  { name: 'Reels', value: 29665 },
  { name: 'Outros', value: 9673 },
  { name: 'Fotos', value: 1459 },
  { name: 'Várias fotos', value: 137 },
  { name: 'Várias mídias', value: 25 },
];

const fbInteractionsData = [
  { name: 'Reels', value: 2454 },
  { name: 'Fotos', value: 122 },
  { name: 'Links', value: 66 },
  { name: 'Stories', value: 30 },
  { name: 'Várias fotos', value: 6 },
];

const genderData = [
  { name: 'Homens', value: 54.8 },
  { name: 'Mulheres', value: 45.2 },
];
const GENDER_COLORS = [MALE_COLOR, FEMALE_COLOR];

const ageData = [
  { faixa: '18–24', homens: 6, mulheres: 7 },
  { faixa: '25–34', homens: 14, mulheres: 13 },
  { faixa: '35–44', homens: 19, mulheres: 16 },
  { faixa: '45–54', homens: 10, mulheres: 8 },
  { faixa: '55–64', homens: 5, mulheres: 4 },
  { faixa: '65+', homens: 1, mulheres: 1 },
];

const citiesData = [
  { city: 'Bagé, RS', pct: 46.1 },
  { city: 'Dom Pedrito, RS', pct: 6.8 },
  { city: 'Pelotas, RS', pct: 5.1 },
  { city: 'Porto Alegre, RS', pct: 3.0 },
  { city: 'Santa Maria, RS', pct: 2.6 },
  { city: 'Candiota, RS', pct: 2.1 },
  { city: 'Hulha Negra, RS', pct: 2.0 },
  { city: "Sant'Ana do Livramento, RS", pct: 1.1 },
  { city: 'São Gabriel, RS', pct: 0.9 },
  { city: 'Uruguaiana, RS', pct: 0.8 },
];

const top10 = [
  { pos: 1, content: '8º Dia de Campo Agricampanha (Foto/Post cruzado)', date: '6 jan', views: '71.737', reach: '92', platform: 'Instagram' },
  { pos: 2, content: '"No campo, não tem espaço pra sorte..." (Reel)', date: '12 jan', views: '34.484', reach: '—', platform: 'Instagram' },
  { pos: 3, content: '"Foi dada a largada para o 8º Dia de Campo..." (Várias mídias)', date: '11 nov 2025', views: '24.319', reach: '84', platform: 'Instagram' },
  { pos: 4, content: '"Dia de Campo da Agricampanha v..." (Reel)', date: '25 fev', views: '20.379', reach: '—', platform: 'Instagram' },
  { pos: 5, content: '"Save the Date: 05 de março de 2026" (Reel)', date: '21 nov 2025', views: '12.754', reach: '7.239', platform: 'Instagram' },
  { pos: 6, content: '"Já estamos presentes na 36ª Abertura O..." (Carrossel)', date: '25 fev', views: '10.711', reach: '—', platform: 'Instagram' },
  { pos: 7, content: '"Vem aí o 8º Dia de Campo..." (Foto)', date: '6 nov 2025', views: '9.807', reach: '3.527', platform: 'Facebook' },
  { pos: 8, content: '"Hoje tivemos a alegria de levar nossa mú..." (Foto)', date: '6 mar', views: '9.639', reach: '—', platform: 'Instagram' },
  { pos: 9, content: '"Recado importante do @richardsel..." (Reel/Post cruzado)', date: '26 fev', views: '9.292', reach: '21', platform: 'Instagram' },
  { pos: 10, content: '"Acumulado de chuvas em Bagé-RS..." (Foto)', date: '9 dez 2025', views: '9.180', reach: '5.423', platform: 'Facebook' },
];

const fmt = (n: number) => n.toLocaleString('pt-BR');

// ── Dashboard ────────────────────────────────────────────────────────────────
const SocialDashboard: React.FC = () => {
  const token = localStorage.getItem('admin_token');
  if (!token) return <Navigate to="/admin/login" replace />;


  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{ color: PRIMARY_DARK }}>
            <i className="bi bi-bar-chart-line me-2" style={{ color: PRIMARY }}></i>
            Relatório de Social Media
          </h2>
          <small className="text-muted">
            Período: <strong>1 nov 2025 – 31 mar 2026</strong>
            &nbsp;·&nbsp; Comparativo: 3 jun – 31 out 2025
          </small>
        </div>
        <span className="badge" style={{ background: PRIMARY, fontSize: '0.75rem', padding: '6px 12px' }}>
          Meta Business Suite
        </span>
      </div>

      {/* ── Destaques ── */}
      <Section icon="lightbulb" title="Destaques e Observações" color={ACCENT} />
      <Row className="g-3 mb-4">
        {[
          { icon: 'instagram', color: IG_COLOR, text: 'Instagram foi a plataforma de maior performance — 752k visualizações versus 89,8k no Facebook. O crescimento orgânico (+379%) superou significativamente o Facebook orgânico (+184%).' },
          { icon: 'calendar-event', color: PRIMARY, text: 'O 8º Dia de Campo Agricampanha foi o tema dominante — o post de 6 de janeiro alcançou 71,7k visualizações com 447 curtidas e 242 compartilhamentos.' },
          { icon: 'play-circle', color: FB_COLOR, text: 'Reels e Carrosseis performam melhor. No Facebook, Reels sozinhos geraram 2.454 das 2.700 interações totais (+90% do total).' },
          { icon: 'geo-alt', color: ACCENT, text: 'Público altamente regional — Bagé/RS representa 46,1% dos seguidores, confirmando a identidade da marca com o agronegócio da região.' },
          { icon: 'person-plus', color: GREEN_LIGHT, text: '+127,8% de novos seguidores no Instagram (1.100 no período), indicando que a estratégia está convertendo visualizações em audiência fidelizada.' },
        ].map(({ icon, color, text }, i) => (
          <Col md={4} key={i}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12, borderLeft: `4px solid ${color}` }}>
              <Card.Body className="d-flex gap-3 align-items-start p-3">
                <i className={`bi bi-${icon}`} style={{ color, fontSize: '1.4rem', flexShrink: 0, marginTop: 2 }}></i>
                <p className="mb-0" style={{ fontSize: '0.87rem', lineHeight: 1.5 }}>{text}</p>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Instagram KPIs ── */}
      <Section icon="instagram" title="Instagram" color={IG_COLOR} />
      <Row className="g-3 mb-2">
        <Col xs={6} md={3}><Kpi icon="eye" label="Visualizações totais" value={fmt(752029)} change="+450%" color={IG_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="eye-fill" label="Orgânico" value={fmt(659193)} change="+379,2%" color={IG_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="megaphone" label="Alcance" value={fmt(114400)} change="+146,4%" color={IG_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="hand-thumbs-up" label="Interações" value="8.900" change="+748,6%" color={IG_COLOR} /></Col>
      </Row>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}><Kpi icon="link-45deg" label="Cliques no link" value="3.300" change="+9.700%" color={IG_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="person-circle" label="Visitas ao perfil" value="8.800" change="+198,7%" color={IG_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="person-plus" label="Novos seguidores" value="1.100" change="+127,8%" color={IG_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="people" label="Total de seguidores" value="5.938" color={IG_COLOR} /></Col>
      </Row>

      {/* ── Facebook KPIs ── */}
      <Section icon="facebook" title="Facebook" color={FB_COLOR} />
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}><Kpi icon="eye" label="Visualizações totais" value={fmt(89757)} change="+7.100%" color={FB_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="eye-fill" label="Orgânico" value={fmt(3548)} change="+184,5%" color={FB_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="hand-thumbs-up" label="Interações" value="2.700" change="+6.800%" color={FB_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="link-45deg" label="Cliques no link" value="2.900" change="+100%" color={FB_COLOR} /></Col>
      </Row>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}><Kpi icon="clock" label="Tempo de visualização" value="3h 19min" change="+773,1%" color={FB_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="person-circle" label="Visitas à página" value="474" change="+532%" color={FB_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="person-plus" label="Novos seguidores" value="20" change="+566,7%" color={FB_COLOR} /></Col>
        <Col xs={6} md={3}><Kpi icon="people" label="Total de seguidores" value="67" color={FB_COLOR} /></Col>
      </Row>

      {/* ── Gráfico: Plataformas ── */}
      <Section icon="bar-chart" title="Comparativo de Plataformas — Visualizações" />
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <Card.Body>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={platformData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 13 }} />
              <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Legend />
              <Bar dataKey="organico" name="Orgânico" fill={IG_COLOR} radius={[4,4,0,0]} />
              <Bar dataKey="anuncios" name="Anúncios" fill={FB_COLOR} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {/* ── Gráficos: Formatos Facebook ── */}
      <Section icon="facebook" title="Facebook — Visualizações e Interações por Formato" color={FB_COLOR} />
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
            <Card.Body>
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>Visualizações por formato</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={fbFormatsData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  <Bar dataKey="value" name="Visualizações" fill={FB_COLOR} radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
            <Card.Body>
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>Interações por formato</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={fbInteractionsData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  <Bar dataKey="value" name="Interações" fill={FB_COLOR} radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Público Instagram ── */}
      <Section icon="people" title="Público — Instagram" color={IG_COLOR} />
      <Row className="g-3 mb-4">
        {/* Gênero */}
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
            <Card.Body>
              <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>Distribuição por gênero</p>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={genderData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value">
                    {genderData.map((_, i) => <Cell key={i} fill={GENDER_COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${v}%`} />
                  <Legend formatter={(value, entry: any) => `${value} ${entry.payload.value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        {/* Faixa etária */}
        <Col md={8}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
            <Card.Body>
              <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>Faixa etária (% estimado)</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ageData} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="faixa" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip formatter={(v: number) => `${v}%`} />
                  <Legend />
                  <Bar dataKey="homens" name="Homens" fill={MALE_COLOR} radius={[4,4,0,0]} />
                  <Bar dataKey="mulheres" name="Mulheres" fill={FEMALE_COLOR} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Cidades ── */}
      <Row className="g-3 mb-4">
        <Col md={7}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
            <Card.Body>
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>Top cidades — Instagram</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={citiesData} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" unit="%" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="city" tick={{ fontSize: 10 }} width={150} />
                  <Tooltip formatter={(v: number) => `${v}%`} />
                  <Bar dataKey="pct" name="%" fill={IG_COLOR} radius={[0,4,4,0]}>
                    {citiesData.map((_, i) => <Cell key={i} fill={i === 0 ? '#833AB4' : IG_COLOR} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col md={5}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
            <Card.Body>
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>Principais países</p>
              <div className="d-flex flex-column gap-3 mt-2">
                {[
                  { country: '🇧🇷 Brasil', pct: 98.2 },
                  { country: '🇺🇾 Uruguai', pct: 1.3 },
                  { country: '🇦🇷 Argentina', pct: 0.1 },
                  { country: '🇺🇸 EUA', pct: 0.1 },
                  { country: '🇵🇾 Paraguai', pct: 0.1 },
                ].map(({ country, pct }) => (
                  <div key={country}>
                    <div className="d-flex justify-content-between mb-1">
                      <small>{country}</small>
                      <small className="fw-semibold">{pct}%</small>
                    </div>
                    <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: IG_COLOR, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>

              <hr className="my-3" />
              <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>Publicações no período</p>
              <Row className="g-2 text-center">
                {[
                  { label: 'Stories', value: 396, color: IG_COLOR },
                  { label: 'Posts', value: 188, color: IG_COLOR },
                  { label: 'Fotos FB', value: 127, color: FB_COLOR },
                  { label: 'Reels FB', value: 36, color: FB_COLOR },
                ].map(({ label, value, color }) => (
                  <Col xs={6} key={label}>
                    <div style={{ background: color + '12', borderRadius: 8, padding: '8px 4px' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: 700, color }}>{value}</div>
                      <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>{label}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Top 10 Posts ── */}
      <Section icon="trophy" title="Top 10 Posts — Maior Alcance no Período" color={ACCENT} />
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 12, overflow: 'hidden' }}>
        <Table responsive hover className="mb-0" style={{ fontSize: '0.85rem' }}>
          <thead style={{ background: PRIMARY_DARK, color: '#fff' }}>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Conteúdo</th>
              <th style={{ width: 100 }}>Data</th>
              <th style={{ width: 100 }}>Visualizações</th>
              <th style={{ width: 80 }}>Alcance</th>
              <th style={{ width: 100 }}>Plataforma</th>
            </tr>
          </thead>
          <tbody>
            {top10.map(row => (
              <tr key={row.pos}>
                <td>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    background: row.pos <= 3 ? '#F59E0B' : '#e9ecef',
                    color: row.pos <= 3 ? '#fff' : '#6c757d',
                    fontWeight: 700, fontSize: '0.75rem',
                  }}>{row.pos}</span>
                </td>
                <td style={{ maxWidth: 320 }}>{row.content}</td>
                <td>{row.date}</td>
                <td className="fw-semibold">{row.views}</td>
                <td>{row.reach}</td>
                <td>
                  <Badge style={{ background: row.platform === 'Instagram' ? IG_COLOR : FB_COLOR, fontWeight: 500 }}>
                    <i className={`bi bi-${row.platform.toLowerCase()} me-1`}></i>
                    {row.platform}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

    </div>
  );
};

export default SocialDashboard;
